import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import Layout from '../components/Layout';
import { useLocation } from 'react-router-dom';

const ACCENT = '#6366F1';
const ACCENT_LIGHT = '#EEF2FF';
const ACCENT_BORDER = '#C7D2FE';
const TEXT = '#0F1117';
const MUTED = '#6B7280';
const SURFACE = '#F8F9FF';

const slides = [
  // 1 — Cover
  {
    id: 'cover',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', position: 'relative', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <img src="/new-logo.png" alt="InfyniHub" style={{ height: '120px', objectFit: 'contain', marginBottom: '32px' }} />
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3.2rem' }, color: TEXT, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 2 }}>
             Introduction
          </Typography>
          <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: { xs: '1rem', md: '1.3rem' }, mb: 3 }}>
            Infinite Solutions & Services. One Hub.
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: { xs: '0.9rem', md: '1rem' }, maxWidth: 480, mx: 'auto', lineHeight: 1.8 }}>
            Helping small and medium businesses replace manual workflows with purpose-built digital systems and end-to-end IT services.
          </Typography>
          <Box sx={{ mt: 4, display: 'inline-flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {['SME Focused', 'End-to-End Delivery', 'Partner, Not Vendor'].map(t => (
              <Box key={t} sx={{ px: 2, py: 0.6, borderRadius: '100px', bgcolor: ACCENT_LIGHT, border: `1px solid ${ACCENT_BORDER}` }}>
                <Typography sx={{ color: ACCENT, fontSize: '0.78rem', fontWeight: 700 }}>{t}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', mt: 5 }}>admin@infynihub.com · infynihub.com</Typography>
        </Box>
      </Box>
    ),
  },

  // 2 — The Problem
  {
    id: 'problem',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4 }}>
        <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>The Problem We Solve</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.6rem' }, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 4, maxWidth: 700 }}>
          SMEs are running real businesses on tools that were never built for them.
        </Typography>
        <Grid container spacing={2.5}>
          {[
            { icon: '📊', title: 'Spreadsheet Overload', body: 'Inventory, warranties, production records — all managed in Excel files that break, get lost, or only one person understands.' },
            { icon: '💬', title: 'WhatsApp as a System', body: 'Job updates, customer issues, and team coordination happening in group chats with no structure, no history, no accountability.' },
            { icon: '🏢', title: 'Enterprise Tools Don\'t Fit', body: 'Every "solution" they find is built for companies 10× their size — too complex, too expensive, impossible to implement without a dedicated IT team.' },
            { icon: '📉', title: 'Growth Gets Capped', body: 'Operational chaos limits how fast a business can grow. Manual processes that worked at 5 people break down at 20.' },
          ].map(p => (
            <Grid item xs={12} sm={6} key={p.title}>
              <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: SURFACE, border: `1px solid #E5E7EB`, height: '100%' }}>
                <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>{p.icon}</Typography>
                <Typography sx={{ fontWeight: 800, color: TEXT, fontSize: '0.95rem', mb: 0.75 }}>{p.title}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.85rem', lineHeight: 1.7 }}>{p.body}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ),
  },

  // 3 — Who We Are
  {
    id: 'about',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4 }}>
        <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Who We Are</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.6rem' }, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 3 }}>
          Not a vendor. A partner.
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: { xs: '0.95rem', md: '1.05rem' }, lineHeight: 1.85, maxWidth: 680, mb: 5 }}>
          We found SMEs still using traditional ways to manage business operations — struggling with enterprise tools and minimising their scale because of operational headaches. We built InfyniHub to be their partner, not just another software vendor.
        </Typography>
        <Grid container spacing={2.5}>
          {[
            { v: 'SME', l: 'Exclusively focused' },
            { v: '2', l: 'Proprietary SaaS products' },
            { v: '5+', l: 'IT service categories' },
            { v: 'E2E', l: 'End-to-end delivery' },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.l}>
              <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: SURFACE, border: `1px solid ${ACCENT_BORDER}`, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: ACCENT, letterSpacing: '-0.02em', lineHeight: 1 }}>{s.v}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.78rem', mt: 0.75 }}>{s.l}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ),
  },

  // 4 — GearDesk
  {
    id: 'geardesk',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: ACCENT_LIGHT, border: `1px solid ${ACCENT_BORDER}`, borderRadius: '100px', px: 2, py: 0.5, mb: 2, alignSelf: 'flex-start' }}>
          <Typography sx={{ color: ACCENT, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Product 01</Typography>
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: TEXT, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>GearDesk</Typography>
        <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>End-to-End Customer Support Ticketing System</Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, maxWidth: 640, mb: 4 }}>
          Built for manufacturers who support their own products. Customers raise tickets for product issues, engineers get auto-assigned, and the entire support cycle is managed end-to-end — replacing email chains and WhatsApp groups.
        </Typography>
        <Grid container spacing={1.5}>
          {[
            'Customer self-service ticket portal',
            'Auto engineer assignment by rules',
            'Real-time chat on every ticket',
            'Instant notifications at every stage',
            'File & evidence uploads',
            'Full audit log & change history',
            'Analytics & resolution reporting',
            'Admin dashboard & escalation tools',
          ].map(f => (
            <Grid item xs={12} sm={6} key={f}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: ACCENT_LIGHT, border: `1px solid ${ACCENT_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckIcon sx={{ fontSize: 11, color: ACCENT }} />
                </Box>
                <Typography sx={{ color: '#374151', fontSize: '0.85rem' }}>{f}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ),
  },

  // 5 — LineDesk
  {
    id: 'linedesk',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: ACCENT_LIGHT, border: `1px solid ${ACCENT_BORDER}`, borderRadius: '100px', px: 2, py: 0.5, mb: 2, alignSelf: 'flex-start' }}>
          <Typography sx={{ color: ACCENT, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Product 02</Typography>
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: TEXT, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>LineDesk</Typography>
        <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>Production Streamline System</Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, maxWidth: 640, mb: 4 }}>
          Built for manufacturers tracking product quality, warranties, and service history on Excel or paper. LineDesk replaces that with a clean, searchable digital system — so your team spends less time hunting records and more time on the floor.
        </Typography>
        <Grid container spacing={1.5}>
          {[
            'Digital product database',
            'Warranty lifecycle tracking',
            'Quality control logs',
            'Instant search & filters',
            'Service history per unit',
            'Batch & production tracking',
            'Compliance documentation',
            'Mobile-friendly interface',
          ].map(f => (
            <Grid item xs={12} sm={6} key={f}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: ACCENT_LIGHT, border: `1px solid ${ACCENT_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckIcon sx={{ fontSize: 11, color: ACCENT }} />
                </Box>
                <Typography sx={{ color: '#374151', fontSize: '0.85rem' }}>{f}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ),
  },

  // 6 — IT Services
  {
    id: 'services',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4 }}>
        <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>IT Services</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.6rem' }, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 4 }}>
          Full-stack IT services for your business.
        </Typography>
        <Grid container spacing={2}>
          {[
            { icon: '🖥️', title: 'IT Consulting', body: 'Digital transformation roadmap tailored to your operations, budget, and team.' },
            { icon: '🌐', title: 'Website Development', body: 'Custom, fast, mobile-first websites built to convert visitors into leads.' },
            { icon: '☁️', title: 'DevOps Support', body: 'Cloud infrastructure, CI/CD pipelines, and server management.' },
            { icon: '🔐', title: 'Software Licensing', body: 'Microsoft 365, Google Workspace, Zoho, Antivirus & MDM — procured and configured.' },
            { icon: '💻', title: 'Hardware Sourcing', body: 'Enterprise laptops, firewalls, switches, and routers — sourced and deployed.' },
          ].map(s => (
            <Grid item xs={12} sm={6} md={4} key={s.title}>
              <Box sx={{ p: 2.5, borderRadius: '12px', bgcolor: SURFACE, border: '1px solid #E5E7EB', height: '100%' }}>
                <Typography sx={{ fontSize: '1.4rem', mb: 1 }}>{s.icon}</Typography>
                <Typography sx={{ fontWeight: 800, color: TEXT, fontSize: '0.9rem', mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.82rem', lineHeight: 1.7 }}>{s.body}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ),
  },

  // 7 — How We Work
  {
    id: 'process',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4 }}>
        <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Our Approach</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.6rem' }, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 5 }}>
          How we work with you.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { num: '01', title: 'We start with your process', body: 'Before recommending anything, we understand how your team actually works today — the spreadsheets, the WhatsApp groups, the paper forms.' },
            { num: '02', title: 'We build or configure the right system', body: "Whether that's one of our SaaS products or a custom setup using third-party tools, we implement it properly — not just hand you a login." },
            { num: '03', title: 'We stay with you', body: "Onboarding, training, ongoing support. We're not a one-time vendor. We're the IT partner your business can actually rely on." },
          ].map((s, i, arr) => (
            <Box key={s.num} sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff' }}>{s.num}</Typography>
                </Box>
                {i < arr.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 28, bgcolor: ACCENT_BORDER, my: 0.5 }} />}
              </Box>
              <Box sx={{ pb: i < arr.length - 1 ? 4 : 0, pt: 0.75 }}>
                <Typography sx={{ fontWeight: 800, color: TEXT, fontSize: '1.05rem', mb: 0.75 }}>{s.title}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.88rem', lineHeight: 1.75 }}>{s.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    ),
  },

  // 8 — CTA / Contact
  {
    id: 'contact',
    render: () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', bgcolor: '#FFFFFF', px: { xs: 3, md: 6 }, py: 4, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 600 }}>
          <img src="/new-logo.png" alt="InfyniHub" style={{ height: '100px', objectFit: 'contain', marginBottom: '28px' }} />
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.8rem' }, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 2 }}>
            Let's talk about your business.
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '1rem', lineHeight: 1.85, mb: 5 }}>
            Tell us what you're working with today. We'll come back with a practical recommendation — no sales pitch, no obligation.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            {[
              { label: 'Email', value: 'admin@infynihub.com' },
              { label: 'Website', value: 'www.infynihub.com' },
              { label: 'LinkedIn', value: 'linkedin.com/in/infynihub-com-aa29633ab' },
            ].map(c => (
              <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, minWidth: 64, textAlign: 'right' }}>{c.label}</Typography>
                <Typography sx={{ color: TEXT, fontWeight: 700, fontSize: '0.95rem' }}>{c.value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 5, pt: 4, borderTop: '1px solid #F3F4F6' }}>
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>© {new Date().getFullYear()} InfyniHub · Infinite Solutions & Services. One Hub.</Typography>
          </Box>
        </Box>
      </Box>
    ),
  },
];

const slideLabels = ['Cover', 'Problem', 'About Us', 'GearDesk', 'LineDesk', 'IT Services', 'How We Work', 'Contact'];

const DeckPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const printIndex = params.get('print');
  const isPrintMode = printIndex !== null;
  const printSlideIndex = isPrintMode ? parseInt(printIndex, 10) : 0;

  const [current, setCurrent] = useState(0);
  const total = slides.length;

  useEffect(() => {
    if (isPrintMode) {
      setCurrent(printSlideIndex);
    }
  }, [isPrintMode, printSlideIndex]);

  const printSlide = () => {
    const url = `/deck?print=${current}`;
    const w = window.open(url, '_blank', 'width=1200,height=800');
    w.onload = () => setTimeout(() => { w.print(); }, 1500);
  };

  const printAll = () => {
    const url = `/deck?print=all`;
    const w = window.open(url, '_blank', 'width=1200,height=900');
    w.onload = () => setTimeout(() => { w.print(); }, 2000);
  };

  // Print mode — render only the slide(s), full screen, no chrome
  if (isPrintMode) {
    const isAll = printIndex === 'all';
    return (
      <Box sx={{ bgcolor: '#FFFFFF', width: '100%' }}>
        {(isAll ? slides : [slides[printSlideIndex]]).map((slide, i) => (
          <Box key={i} sx={{
            width: '100%',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 5 },
            boxSizing: 'border-box',
            pageBreakAfter: 'always',
            breakAfter: 'page',
            bgcolor: '#FFFFFF',
          }}>
            <Box sx={{ width: '100%', maxWidth: 960 }}>
              {slide.render()}
            </Box>
          </Box>
        ))}
        <style>{`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            body { margin: 0 !important; }
            @page { margin: 0 !important; size: A4 landscape; }
          }
        `}</style>
      </Box>
    );
  }

  const prev = () => setCurrent(i => Math.max(0, i - 1));
  const next = () => setCurrent(i => Math.min(total - 1, i + 1));

  return (
    <Layout>
      <Box sx={{ bgcolor: '#F3F4F6', pt: { xs: '140px', md: '150px' }, pb: 6, minHeight: '100vh' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>

          {/* Header */}
          <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, color: TEXT, fontSize: { xs: '1.4rem', md: '1.8rem' }, letterSpacing: '-0.02em' }}>Company Deck</Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>Slide {current + 1} of {total} — {slideLabels[current]}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                onClick={printSlide}
                variant="outlined"
                sx={{ borderColor: ACCENT_BORDER, color: ACCENT, fontWeight: 700, textTransform: 'none', borderRadius: '8px', '&:hover': { bgcolor: ACCENT_LIGHT } }}
              >
                Print This Slide
              </Button>
              <Button
                onClick={printAll}
                variant="contained"
                sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', '&:hover': { bgcolor: '#5254CC' } }}
              >
                Print All (PDF)
              </Button>
            </Box>
          </Box>

          {/* Slide */}
          <div id="deck-print-root">
          <Box id="deck-slide-content" sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '20px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
            minHeight: { xs: 'auto', md: '560px' },
            overflow: 'hidden',
          }}>
            {slides[current].render()}
          </Box>
          </div>

          {/* Navigation */}
          <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
            <Button
              onClick={prev}
              disabled={current === 0}
              startIcon={<ArrowBackIcon />}
              sx={{ color: current === 0 ? '#D1D5DB' : TEXT, fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: 'transparent', color: ACCENT } }}
            >
              Previous
            </Button>

            {/* Dot indicators */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {slides.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setCurrent(i)}
                  sx={{
                    width: i === current ? 24 : 8,
                    height: 8,
                    borderRadius: '100px',
                    bgcolor: i === current ? ACCENT : '#D1D5DB',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                  }}
                />
              ))}
            </Box>

            <Button
              onClick={next}
              disabled={current === total - 1}
              endIcon={<ArrowForwardIcon />}
              sx={{ color: current === total - 1 ? '#D1D5DB' : TEXT, fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: 'transparent', color: ACCENT } }}
            >
              Next
            </Button>
          </Box>

          {/* Slide thumbnails */}
          <Box className="no-print" sx={{ display: 'flex', gap: 1.5, mt: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {slideLabels.map((label, i) => (
              <Box
                key={i}
                onClick={() => setCurrent(i)}
                sx={{
                  px: 2, py: 0.75, borderRadius: '8px', cursor: 'pointer',
                  bgcolor: i === current ? ACCENT_LIGHT : '#FFFFFF',
                  border: `1px solid ${i === current ? ACCENT_BORDER : '#E5E7EB'}`,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: ACCENT_BORDER, bgcolor: ACCENT_LIGHT },
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', fontWeight: i === current ? 700 : 500, color: i === current ? ACCENT : MUTED }}>
                  {i + 1}. {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

    </Layout>
  );
};

export default DeckPage;
